import React, { useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Panel from '../../components/common/Panel';
import Wall from '../../components/common/Wall';
import CountTopFilter from '../../components/common/CountTopFilter';
import Button from '../../components/common/Button';
import BrandOrdersContainer from '../blocks/BrandOrdersContainer';
import PageLayout from '../../components/common/PageLayout';
import { setOrderLastDayFilter, setBrandOrdersRetailerFilter } from '../../store/actions/pageFilters';
import {
  ordersReport,
  setBrandOrdersReport,
  getBrandOrders,
  setBrandOrdersLoading,
} from '../../store/actions/brandOrders';
import DownloadReportModal from '../../components/modals/DownloadReportModal';
import CurrencyTypeSelectionModal from '../../components/modals/CurrencyTypeSelectionModal';
import { currencyTypeOptions } from '../../components/modals/CurrencyTypeSelectionModal/currencyTypeOptions';
import DropDownFilter from '../../components/filters/DropDownFilter';
import DateRange from '../../components/common/DateRange/DateRange';
import { reportBusinessActionToMixpanel } from '../../utils/reportBusinessActionToMixpanel';
import { useFlagsmith } from 'flagsmith-react';
import history from '../../utils/history';
import { supplierRetailerUtils } from '../../utils/supplierRetailer';
import { getSupplierRetailersFromStateSelector } from '../../utils/stateHelper';

const CURRENCY_FEATURE_FLAG_KEY = 'supplier_order_report_with_currency';

function OrdersPageContainer(props) {
  const { getBrandOrders, activeCount, isLoadingFilters } = props;
  const initialCurrencyTypeValue = currencyTypeOptions()[0].value;
  const [shouldShowCurrencyModal, setShouldShowCurrencyModal] = useState(false);
  const [currentCheckedCurrencyValue, setCurrentCheckedCurrencyValue] = useState(initialCurrencyTypeValue);
  const supplierId = useSelector((state) => state.role?.supplier?.id);
  const { hasFeature } = useFlagsmith();
  const currencyFeature = hasFeature(CURRENCY_FEATURE_FLAG_KEY);

  const handleChangeRange = (startDate, endDate) => {
    props.setBrandOrdersLoading(false);
    props.setOrderLastDayFilter(startDate, endDate);
  };

  const handleCurrencyChange = (selectedValue) => {
    setCurrentCheckedCurrencyValue(selectedValue);
  };

  const handleGenerateReportClick = () => {
    if (currencyFeature && props.shouldShowCurrencySelection) {
      setShouldShowCurrencyModal(true);
    } else {
      downloadReport();
    }
  };

  const mixpanel = useMixpanel();
  const reportBi = () => {
    reportBusinessActionToMixpanel(mixpanel, 'suppliers-orders-generate-report-button-click');
  };

  const downloadReport = () => {
    setShouldShowCurrencyModal(false);
    props.downloadReport(currentCheckedCurrencyValue === initialCurrencyTypeValue);
    reportBi();
  };

  const disableGenerateReportButton = props.canceledCount <= 0 && props.completedCount <= 0 && props.pendingCount <= 0;

  const pagePanel = (
    <Panel>
      <Wall />
      <DateRange
        onChange={(startDate, endDate) => {
          handleChangeRange(startDate, endDate);
        }}
      />
      <Wall />
      <CountTopFilter
        onClick={getBrandOrders}
        title="ALL"
        count={props.count}
        activeCount={activeCount}
        loading={isLoadingFilters}
      />
      <CountTopFilter
        onClick={getBrandOrders}
        title="PROCESSING"
        count={props.pendingCount}
        activeCount={activeCount}
        loading={isLoadingFilters}
      />
      <CountTopFilter
        onClick={getBrandOrders}
        title="COMPLETED"
        count={props.completedCount}
        activeCount={activeCount}
        loading={isLoadingFilters}
      />
      <CountTopFilter
        onClick={getBrandOrders}
        title="CANCELED"
        count={props.canceledCount}
        activeCount={activeCount}
        loading={isLoadingFilters}
      />
      <Wall />
      <DropDownFilter
        currentIndex={0}
        values={props.channelDropdownSelectorOptions}
        onChange={props.setBrandOrdersRetailerFilter}
      />
      <Wall />
      <Button text="Generate Report" onClick={handleGenerateReportClick} disabled={disableGenerateReportButton} />
      <Wall />
    </Panel>
  );

  const onReportsPageLinkClick = () => {
    props.closeReport();
    history.push(`/suppliers/${supplierId}/analytics/reports`);
  };

  return (
    <PageLayout top={pagePanel}>
      <DownloadReportModal
        open={props.isReport}
        onClose={props.closeReport}
        text={[
          'Your orders report is being emailed to you and will',
          <br />,
          'soon be available on the ',
          <button key="reports-page-link" onClick={onReportsPageLinkClick}>
            Reports page
          </button>,
          '. ',
        ]}
      />
      <CurrencyTypeSelectionModal
        open={shouldShowCurrencyModal}
        onClose={() => setShouldShowCurrencyModal(false)}
        onConfirm={downloadReport}
        onChange={(selectedValue) => handleCurrencyChange(selectedValue)}
        currentCheckedValue={currentCheckedCurrencyValue}
        supplierCurrency={props.supplierCurrency}
      />
      <BrandOrdersContainer />
    </PageLayout>
  );
}

const mapStateToProps = (state) => {
  const {
    count,
    pendingCount,
    completedCount,
    canceledCount,
    isReport,
    activeCount,
    retailers = [],
    isLoadingFilters,
  } = state.brandOrders;

  const shouldShowCurrencyTypeSelection = (state) => {
    const { currency: supplierCurrency, currencies } = state.role?.supplier || {};

    if (!currencies?.length) return false;

    return currencies.some((currency) => currency.toLowerCase() !== supplierCurrency.toLowerCase());
  };

  const shouldShowCurrencySelection = shouldShowCurrencyTypeSelection(state);
  const supplierCurrency = state.role.supplier.currency;

  const supplierRetailers = getSupplierRetailersFromStateSelector(state);

  const channelDropdownSelectorOptions = retailers.map((retailer) => {
    const retailerDisplayName = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
      supplierRetailers,
      retailer,
      retailerId: retailer.value,
    });
    return {
      label: retailerDisplayName || retailer.label,
      value: retailer.value,
    };
  });

  return {
    count,
    pendingCount,
    completedCount,
    canceledCount,
    isReport,
    activeCount,
    retailers,
    channelDropdownSelectorOptions,
    isLoadingFilters,
    shouldShowCurrencySelection,
    supplierCurrency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setOrderLastDayFilter: (startDate, endDate) => dispatch(setOrderLastDayFilter(startDate, endDate)),
  downloadReport: (useOriginalCurrency = true) => dispatch(ordersReport(useOriginalCurrency)),
  closeReport: () => dispatch(setBrandOrdersReport(false)),
  getBrandOrders: (title) => dispatch(getBrandOrders(title)),
  setBrandOrdersRetailerFilter: (value) => dispatch(setBrandOrdersRetailerFilter(value)),
  setBrandOrdersLoading: (value) => dispatch(setBrandOrdersLoading(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersPageContainer);

OrdersPageContainer.defaultProps = {
  isReport: false,
};

OrdersPageContainer.propTypes = {
  closeReport: PropTypes.func.isRequired,
  isReport: PropTypes.bool,
  downloadReport: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  pendingCount: PropTypes.number.isRequired,
  completedCount: PropTypes.number.isRequired,
  canceledCount: PropTypes.number.isRequired,
  setOrderLastDayFilter: PropTypes.func.isRequired,
  getBrandOrders: PropTypes.func.isRequired,
  activeCount: PropTypes.string.isRequired,
  retailers: PropTypes.arrayOf(PropTypes.object).isRequired,
  setBrandOrdersRetailerFilter: PropTypes.func.isRequired,
  shouldShowCurrencySelection: PropTypes.bool,
};
